import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { cx } from "../../../style";
import "./Tabs.css";

const DEFAULT_MAX_VISIBLE = 6;

/**
 * Tabs — единый компонент вкладок второго уровня.
 *
 * Два режима работы:
 *  1) Роутинг: у items есть `to` — рендерятся <Link>, активная определяется по `activeKey`
 *     (или по совпадению `to` с `activePath`).
 *  2) Контролируемый: без `to` — рендерятся кнопки, клик → onChange(key).
 *
 * @param {Array<{
 *   key: string,
 *   label: React.ReactNode,
 *   to?: string,               // путь для <Link> (режим роутинга)
 *   icon?: React.ReactNode,
 *   badge?: React.ReactNode,   // счётчик/маркер
 *   disabled?: boolean
 * }>} items
 * @param {string} [activeKey]       Ключ активной вкладки.
 * @param {string} [activePath]      Текущий путь (альтернатива activeKey для режима роутинга).
 * @param {(key: string, item) => void} [onChange]  Клик по вкладке (контролируемый режим).
 * @param {("pills"|"underline")} [variant="pills"]
 * @param {number} [maxVisible=6]    Сколько вкладок показывать до кнопки «Еще» (0 = без сворачивания).
 * @param {string} [className] @param {object} [style]
 */
export default function Tabs({
  items = [],
  activeKey,
  activePath,
  onChange,
  variant = "pills",
  maxVisible = DEFAULT_MAX_VISIBLE,
  className,
  style,
}) {
  const [expanded, setExpanded] = useState(false);

  const isActive = (item) =>
    (activeKey != null && item.key === activeKey) ||
    (activePath != null && item.to === activePath);

  const activeIndex = items.findIndex(isActive);

  // Свернуть при смене активной вкладки
  useEffect(() => {
    setExpanded(false);
  }, [activeKey, activePath]);

  const collapse = maxVisible > 0 && items.length > maxVisible;

  const visibleItems = useMemo(() => {
    if (!collapse) return items;
    const pinned = [];
    if (activeIndex >= 0) pinned.push(items[activeIndex]);
    for (const it of items) {
      if (pinned.length >= maxVisible) break;
      if (!pinned.some((p) => p.key === it.key)) pinned.push(it);
    }
    return pinned;
  }, [collapse, items, activeIndex, maxVisible]);

  const rendered = expanded ? items : visibleItems;
  const hasHidden = collapse && rendered.length < items.length;

  const renderTab = (item) => {
    const active = isActive(item);
    const classes = cx("ui-tab", active && "ui-tab--active");
    const content = (
      <>
        {item.icon && <span className="ui-tab__icon">{item.icon}</span>}
        {item.label}
        {item.badge != null && <span className="ui-tab__badge">{item.badge}</span>}
      </>
    );

    if (item.to) {
      return (
        <Link
          key={item.key}
          to={item.to}
          className={classes}
          aria-current={active ? "page" : undefined}
          onClick={onChange ? () => onChange(item.key, item) : undefined}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        key={item.key}
        type="button"
        className={classes}
        disabled={item.disabled}
        aria-current={active ? "page" : undefined}
        onClick={() => onChange && onChange(item.key, item)}
      >
        {content}
      </button>
    );
  };

  return (
    <div
      className={cx("ui-tabs", `ui-tabs--${variant}`, className)}
      style={style}
      role="tablist"
    >
      <div className={cx("ui-tabs__row", expanded && "ui-tabs__row--expanded")}>
        <div
          className={cx(
            "ui-tabs__track",
            "ui-scroll-hidden",
            expanded && "ui-tabs__track--wrap",
          )}
        >
          {rendered.map(renderTab)}
        </div>

        {hasHidden && (
          <button
            type="button"
            className="ui-tabs__more"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "Свернуть" : "Еще"}
            {expanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
          </button>
        )}
      </div>
    </div>
  );
}
